import {
  IconBuilding,
  IconCalendar,
  IconCalendarEvent,
  IconClock,
  IconHome,
  IconKey,
  IconUsers,
  IconX,
} from '@tabler/icons-react';
import { ReactNode } from 'react';
import { activeTab } from '../types/dashboard.types';
import { StatsMetrics } from '../models/dashboardModels';
import MetricsDashboardLegacy from './MetricsDashboardLegacy';
import {
  PRE_SALE_STATUS_MAP,
  VISIT_STATUS_MAP,
} from '../adapters/dashboardAdapters';

interface Props {
  activeTab: activeTab;
  stats: StatsMetrics;
  // Estado seleccionado en el filtro ('' = todos). Al hacer clic en una
  // tarjeta se usa el mismo valor que el Select de Estado de la barra de filtros.
  selectedStatus?: string;
  onSelectStatus?: (status: string) => void;
  // Tarjetas adicionales (p. ej. "Falta feedback"), filtran con su `status`.
  extraTiles?: Tile[];
}

type Tone = 'primary' | 'warning' | 'info' | 'success' | 'danger';

export interface Tile {
  key: string;
  label: string;
  value: number;
  status: string; // valor que se envía como filtro ('' = total)
  tone: Tone;
  icon: ReactNode;
}

const MetricsDashboard = ({
  activeTab,
  stats,
  selectedStatus = '',
  onSelectStatus,
  extraTiles = [],
}: Props) => {
  // Sin onSelectStatus (p. ej. ProperDashboard) se mantiene el diseño anterior;
  // el estilo nuevo solo aplica donde el contenedor tiene .dashboard-v2.
  if (!onSelectStatus) {
    return <MetricsDashboardLegacy activeTab={activeTab} stats={stats} />;
  }

  const isPreSale = activeTab === 'pre-sale';
  const statusMap = isPreSale ? PRE_SALE_STATUS_MAP : VISIT_STATUS_MAP;

  const totalLabel = isPreSale ? 'Total asesorías' : 'Total visitas';
  const totalIcon = isPreSale ? (
    <IconHome size={16} />
  ) : activeTab === 'rent' || activeTab === 'secondary' ? (
    <IconKey size={16} />
  ) : (
    <IconUsers size={16} />
  );

  const tiles: Tile[] = [
    {
      key: 'total',
      label: totalLabel,
      value: stats.total,
      status: '',
      tone: 'primary',
      icon: totalIcon,
    },
    {
      key: 'pending_approval',
      label: 'Pendiente de aprobación',
      value: stats.pending_approval,
      status: statusMap.pending_approval,
      tone: 'warning',
      icon: <IconClock size={16} />,
    },
    {
      key: 'scheduled',
      label: 'Programada',
      value: stats.scheduled,
      status: statusMap.scheduled,
      tone: 'info',
      icon: <IconCalendar size={16} />,
    },
    {
      key: 'rescheduled',
      label: 'Re programada',
      value: stats.rescheduled,
      status: statusMap.rescheduled,
      tone: 'warning',
      icon: <IconCalendarEvent size={16} />,
    },
    {
      key: 'done',
      label: 'Finalizada',
      value: stats.done,
      status: statusMap.done,
      tone: 'success',
      icon: <IconBuilding size={16} />,
    },
    {
      key: 'cancelled',
      label: 'Cancelada',
      value: stats.cancelled,
      status: statusMap.cancelled,
      tone: 'danger',
      icon: <IconX size={16} />,
    },
  ];

  if (isPreSale && statusMap.voided) {
    tiles.push({
      key: 'voided',
      label: 'Anulada',
      value: stats.voided,
      status: statusMap.voided,
      tone: 'danger',
      icon: <IconX size={16} />,
    });
  }

  tiles.push(...extraTiles);

  return (
    <div className="metric-grid">
      {tiles.map((tile) => {
        const isSelected = selectedStatus === tile.status;
        return (
          <button
            key={tile.key}
            type="button"
            className={`metric-tile metric-tile--${tile.tone}${
              isSelected ? ' is-selected' : ''
            }`}
            aria-pressed={isSelected}
            onClick={() =>
              onSelectStatus?.(isSelected && tile.status ? '' : tile.status)
            }
          >
            <span className="metric-tile__label">{tile.label}</span>
            <span className="metric-tile__row">
              <span className="metric-tile__icon">{tile.icon}</span>
              <span className="metric-tile__value">{tile.value}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
};

export default MetricsDashboard;
